import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Camera, ArrowLeft, Image as ImageIcon, X } from "lucide-react";

export function GalleryIndex() {
  const [albums, setAlbums] = useState([]);
  useEffect(() => {
    api.get("/albums").then((r) => setAlbums(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="gallery-page">
      <section className="relative overflow-hidden bg-[#0F172A] text-white">
        <div className="absolute -top-20 -right-20 h-80 w-80 bg-[#D97706]/30 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Souvenirs du club</span>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]" style={{ fontFamily: "Outfit" }}>
            Galerie <span className="text-[#D97706]">photos</span>.
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-xl">
            Photos d'équipes, moments de terrain, tournois, stages — revivez les meilleurs instants du Villeurbanne United.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {albums.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-white p-12 text-center" data-testid="albums-empty">
            <Camera className="h-10 w-10 text-[#CBD5E1] mx-auto" />
            <p className="mt-4 font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>Pas encore de photos</p>
            <p className="mt-2 text-sm text-[#475569] max-w-md mx-auto">
              Les parents et les coachs peuvent envoyer leurs photos de match ou d'équipe à <a href="mailto:vufc69100@yahoo.com" className="font-semibold text-[#1E3A8A] hover:text-[#D97706]">vufc69100@yahoo.com</a>,
              elles seront publiées ici après validation.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="albums-grid">
            {albums.map((a) => (
              <Link
                key={a.id}
                to={`/galerie/${a.slug}`}
                data-testid={`album-${a.id}`}
                className="group rounded-lg overflow-hidden border border-[#E2E8F0] bg-white hover:-translate-y-1 hover:border-[#1E3A8A] transition-all duration-200"
              >
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden relative">
                  {a.cover_photo_id ? (
                    <img
                      src={`${API}/photos/${a.cover_photo_id}/image`}
                      alt={a.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]">
                      <Camera className="h-10 w-10 text-white/40" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3 px-2 py-0.5 text-[10px] font-bold rounded bg-black/60 text-white backdrop-blur">
                    {a.photo_count} photo{a.photo_count > 1 ? "s" : ""}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-[#0F172A] group-hover:text-[#1E3A8A] transition-colors" style={{ fontFamily: "Outfit" }}>{a.name}</h3>
                  {a.description && <p className="mt-1 text-sm text-[#475569] line-clamp-2">{a.description}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-10 p-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <ImageIcon className="h-5 w-5 text-[#1E3A8A] shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>Vous avez de belles photos ?</div>
              <p className="text-sm text-[#475569]">Parents et coachs : envoyez-les à <a href="mailto:vufc69100@yahoo.com" className="font-semibold text-[#1E3A8A] hover:text-[#D97706]">vufc69100@yahoo.com</a>, elles seront publiées ici.</p>
            </div>
          </div>
          <a href="mailto:vufc69100@yahoo.com?subject=Photos pour la galerie" className="px-5 py-2.5 rounded-md bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors" data-testid="gallery-send-photos">
            Envoyer des photos
          </a>
        </div>
      </section>
    </div>
  );
}

export function AlbumView() {
  const { slug } = useParams();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/albums/${slug}`).then((r) => {
      setAlbum(r.data.album);
      setPhotos(r.data.photos);
    }).catch((e) => setErr(e.response?.data?.detail || "Erreur"));
  }, [slug]);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  if (err) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-[#DC2626] font-semibold">{err}</div>;
  if (!album) return <div className="max-w-3xl mx-auto px-4 py-16 text-[#475569]">Chargement...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="album-view">
      <Link to="/galerie" className="inline-flex items-center gap-2 text-sm text-[#1E3A8A] font-semibold hover:text-[#D97706] transition-colors" data-testid="back-to-albums">
        <ArrowLeft size={14} /> Tous les albums
      </Link>
      <div className="mt-6 mb-8">
        <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">{photos.length} photo{photos.length > 1 ? "s" : ""}</span>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0F172A] tracking-tighter leading-[1.1]" style={{ fontFamily: "Outfit" }}>
          {album.name}
        </h1>
        {album.description && <p className="mt-3 text-[#475569] max-w-2xl">{album.description}</p>}
      </div>

      {photos.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#E2E8F0] p-10 text-center text-sm text-[#475569]">
          Aucune photo dans cet album pour l'instant.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photos-grid">
          {photos.map((p) => (
            <button
              key={p.id}
              onClick={() => setLightbox(p)}
              className="aspect-square overflow-hidden rounded-md bg-slate-100 group relative"
              data-testid={`photo-${p.id}`}
            >
              <img
                src={`${API}/photos/${p.id}/image`}
                alt={p.caption || album.name}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              {p.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent text-white text-xs text-left opacity-0 group-hover:opacity-100 transition-opacity">
                  {p.caption}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="lightbox"
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={() => setLightbox(null)}
            aria-label="Fermer"
            data-testid="lightbox-close"
          >
            <X size={22} />
          </button>
          <img
            src={`${API}/photos/${lightbox.id}/image`}
            alt={lightbox.caption}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[95vw] object-contain rounded-md"
          />
          {lightbox.caption && (
            <div className="absolute bottom-6 left-0 right-0 text-center text-white text-sm px-4">
              {lightbox.caption}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
